"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "default", {
    enumerable: true,
    get: function() {
        return PresignedUrl;
    }
});
const _nanoid = require("nanoid");
const _contentdisposition = /*#__PURE__*/ _interop_require_default(require("content-disposition"));
const _slash = /*#__PURE__*/ _interop_require_default(require("slash"));
const _nocodbsdk = require("nocodb-sdk");
const _common = require("@nestjs/common");
const _NcPluginMgrv2 = /*#__PURE__*/ _interop_require_default(require("~/helpers/NcPluginMgrv2"));
const _Noco = /*#__PURE__*/ _interop_require_default(require("~/Noco"));
const _NocoCache = /*#__PURE__*/ _interop_require_default(require("~/cache/NocoCache"));
const _globals = require("~/utils/globals");
const _attachmentHelpers = require("~/helpers/attachmentHelpers");
const _modelUtils = require("~/utils/modelUtils");
const _dataUtils = require("~/utils/dataUtils");
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {}, sourceKeys = Object.getOwnPropertyNames(source), key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
        target[key] = source[key];
    }
    return target;
}
function roundExpiry(date) {
    const msInTenMinutes = 10 * 60 * 1000;
    return new Date(Math.ceil(date.getTime() / msInTenMinutes) * msInTenMinutes);
}
const logger = new _common.Logger('Presigned URL');
const DEFAULT_EXPIRE_SECONDS = isNaN(parseInt(process.env.NC_ATTACHMENT_EXPIRE_SECONDS)) ? 2 * 60 * 60 : parseInt(process.env.NC_ATTACHMENT_EXPIRE_SECONDS);
let PresignedUrl = class PresignedUrl {
    static async add(param) {
        const { path, url, expires_at, expiresInSeconds = DEFAULT_EXPIRE_SECONDS } = param;
        await _NocoCache.default.setExpiring('root', `${_globals.CacheScope.PRESIGNED_URL}:path:${(0, _slash.default)(path)}`, {
            path,
            url,
            expires_at
        }, expiresInSeconds);
        await _NocoCache.default.setExpiring('root', `${_globals.CacheScope.PRESIGNED_URL}:url:${(0, _slash.default)(decodeURIComponent(url))}`, {
            path,
            url,
            expires_at
        }, expiresInSeconds);
    }
    static async delete(param) {
        const { path, url } = param;
        await _NocoCache.default.del('root', `${_globals.CacheScope.PRESIGNED_URL}:path:${(0, _slash.default)(path)}`);
        await _NocoCache.default.del('root', `${_globals.CacheScope.PRESIGNED_URL}:url:${(0, _slash.default)(url)}`);
    }
    static async getPath(url, _ncMeta = _Noco.default.ncMeta) {
        const urlData = url && await _NocoCache.default.get('root', `${_globals.CacheScope.PRESIGNED_URL}:url:${(0, _slash.default)(url)}`, _globals.CacheGetType.TYPE_OBJECT);
        if (!urlData) {
            return null;
        }
        // if present, check if the expiry date is greater than now
        if (urlData && new Date(urlData.expires_at).getTime() < new Date().getTime()) {
            // if not, delete the url
            await this.delete({
                path: urlData.path,
                url: urlData.url
            });
            return null;
        }
        return urlData == null ? void 0 : urlData.path;
    }
    static async getSignedUrl(param, ncMeta = _Noco.default.ncMeta) {
        const isUrl = /^https?:\/\//i.test(param.pathOrUrl);
        let path = (isUrl ? (0, _attachmentHelpers.getPathFromUrl)(param.pathOrUrl) : param.pathOrUrl).replace(/^\/+/, '');
        const { expireSeconds = DEFAULT_EXPIRE_SECONDS, filename, mimetype, encoding } = param;
        const preview = param.preview ? (0, _attachmentHelpers.isPreviewAllowed)({
            path,
            mimetype
        }) : false;
        const expireAt = roundExpiry(new Date(new Date().getTime() + expireSeconds * 1000)); // at least expireSeconds from now
        // calculate the expiry time in seconds considering rounding
        const expiresInSeconds = Math.ceil((expireAt.getTime() - new Date().getTime()) / 1000);
        let tempUrl;
        const pathParameters = {
            expireAt: expireAt.toISOString()
        };
        if (preview) {
            pathParameters.ResponseContentDisposition = `inline;`;
            if (filename) {
                pathParameters.ResponseContentDisposition = (0, _contentdisposition.default)(filename, {
                    type: 'inline'
                });
            }
        } else {
            pathParameters.ResponseContentDisposition = `attachment;`;
            if (filename) {
                pathParameters.ResponseContentDisposition = (0, _contentdisposition.default)(filename, {
                    type: 'attachment'
                });
            }
        }
        if (mimetype) {
            pathParameters.ResponseContentType = mimetype;
            if (encoding) {
                pathParameters.ResponseContentType = `${mimetype}; charset=${encoding}`;
            }
        }
        if (encoding) {
            pathParameters.ResponseContentEncoding = encoding;
        }
        // append query params to the cache path
        const cachePath = `${path}?${new URLSearchParams(pathParameters).toString()}`;
        const url = await _NocoCache.default.get('root', `${_globals.CacheScope.PRESIGNED_URL}:path:${(0, _slash.default)(cachePath)}`, _globals.CacheGetType.TYPE_OBJECT);
        if (url) {
            // if present, check if the expiry date is greater than now
            if (new Date(url.expires_at).getTime() > new Date().getTime()) {
                // if greater, return the url
                return url.url;
            } else {
                // if not, delete the url
                await this.delete({
                    path: url.path,
                    url: url.url
                });
            }
        }
        const storageAdapter = await _NcPluginMgrv2.default.storageAdapter(ncMeta);
        if (typeof storageAdapter.getSignedUrl === 'function') {
            tempUrl = await storageAdapter.getSignedUrl(path, expiresInSeconds, pathParameters);
            await this.add({
                path: cachePath,
                url: tempUrl,
                expires_at: expireAt,
                expiresInSeconds
            });
        } else {
            // if not present, use url or generate url for local storage
            tempUrl = isUrl ? param.pathOrUrl : `dltemp/${(0, _nanoid.nanoid)(16)}/${expireAt.getTime()}/${path}`;
            path = `${path}?${new URLSearchParams(pathParameters).toString()}`;
            await this.add({
                path: path,
                url: tempUrl,
                expires_at: expireAt,
                expiresInSeconds
            });
        }
        // return the url
        return tempUrl;
    }
    static async signAttachment(param, ncMeta = _Noco.default.ncMeta) {
        const { nestedKeys = [], attachment, preview = true, mimetype } = param, extra = _object_without_properties_loose(param, [
            "nestedKeys",
            "attachment",
            "preview",
            "mimetype"
        ]);
        const nestedObj = nestedKeys.reduce((acc, key)=>{
            if (acc[key]) {
                return acc[key];
            }
            acc[key] = {};
            return acc[key];
        }, attachment);
        if (attachment == null ? void 0 : attachment.path) {
            nestedObj.signedPath = await PresignedUrl.getSignedUrl(_extends({
                pathOrUrl: attachment.path.replace(/^download[/\\]/i, ''),
                preview,
                mimetype: mimetype || attachment.mimetype
            }, extra ? _extends({}, extra) : {}), ncMeta);
        } else if ((attachment == null ? void 0 : attachment.url) && attachment.status !== 'uploading') {
            nestedObj.signedUrl = await PresignedUrl.getSignedUrl(_extends({
                pathOrUrl: attachment.url,
                preview,
                mimetype: mimetype || attachment.mimetype
            }, extra ? _extends({}, extra) : {}), ncMeta);
        }
    }
    static async signMetaIconImage(data) {
        if (!data) return;
        const allItems = [];
        try {
            for (const d of Array.isArray(data) ? data : [
                data
            ]){
                if (!(0, _nocodbsdk.ncIsObject)(d) || !d.meta) {
                    continue;
                }
                d.meta = (0, _modelUtils.parseMetaProp)(d);
                if (d.meta && d.meta.icon && d.meta.iconType === _nocodbsdk.IconType.IMAGE) {
                    allItems.push({
                        attachment: d.meta.icon
                    });
                }
            }
            await (0, _dataUtils.processConcurrently)(allItems, async (item)=>{
                try {
                    await PresignedUrl.signAttachment(item);
                } catch (e) {}
            }, 15);
        } catch (e) {
            logger.error('Error signing meta icon image', e);
        }
    }
    constructor(data){
        Object.assign(this, data);
    }
};
